/**
 * Utility for observing message visibility in viewport
 * Used for dynamic image loading and performance optimization
 */

/**
 * Check if an element is visible in the viewport
 * @param {HTMLElement} element - The DOM element to check
 * @param {number} threshold - Percentage of element that must be visible (0-1)
 * @returns {boolean} - True if element is visible
 */
export function isElementVisible(element, threshold = 0.1) {
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const windowHeight = window.innerHeight || document.documentElement.clientHeight;
  const windowWidth = window.innerWidth || document.documentElement.clientWidth;

  // Check if element is in viewport
  const vertInView = (rect.top <= windowHeight) && ((rect.top + rect.height) >= 0);
  const horInView = (rect.left <= windowWidth) && ((rect.left + rect.width) >= 0);

  return vertInView && horInView;
}

/**
 * Create an Intersection Observer for lazy loading images
 * @param {Function} callback - Callback function when element becomes visible
 * @param {Object} options - Intersection Observer options
 * @returns {IntersectionObserver} - The observer instance
 */
export function createVisibilityObserver(callback, options = {}) {
  const defaultOptions = {
    root: null, // viewport
    rootMargin: '50px', // Load images 50px before they enter viewport
    threshold: 0.01 // Trigger when 1% of element is visible
  };

  const observerOptions = { ...defaultOptions, ...options };

  return new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        callback(entry.target, entry);
      }
    });
  }, observerOptions);
}

/**
 * Observe multiple elements for visibility
 * @param {Array<HTMLElement>} elements - Array of elements to observe
 * @param {Function} callback - Callback when element becomes visible
 * @param {Object} options - Observer options
 * @returns {IntersectionObserver} - The observer instance
 */
export function observeElements(elements, callback, options = {}) {
  const observer = createVisibilityObserver(callback, options);
  
  elements.forEach(element => {
    if (element) {
      observer.observe(element);
    }
  });

  return observer;
}

/**
 * Unobserve and disconnect an observer
 * @param {IntersectionObserver} observer - The observer to disconnect
 */
export function disconnectObserver(observer) {
  if (observer) {
    observer.disconnect();
  }
}
